import React, { useState, useEffect } from 'react';
import {
  IonIcon, IonBadge, IonButton, IonPopover, IonList, IonItem, IonLabel,
  IonToggle, IonSelect, IonSelectOption, IonItemDivider,
} from '@ionic/react';
import {
  chevronDownOutline, chevronForwardOutline, reorderFourOutline,
  ellipsisVertical, trashOutline, settingsOutline,
  textOutline, removeOutline, lockClosedOutline, lockOpenOutline,
  playOutline, swapVerticalOutline,
} from 'ionicons/icons';
import { useDroppable } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import type { DashboardWidget, SectionConfig } from '../../services/dashboard';
import { getColumnWidths, getWidgetsByColumn } from '../../services/dashboard';
import SectionColumn from './SectionColumn';
import './widgets.css';

interface SectionInfo {
  id: string;
  label: string;
}

interface SectionWidgetProps {
  widget: DashboardWidget;
  householdId: string;
  editMode?: boolean;
  allWidgets?: DashboardWidget[]; // All widgets to find children
  isExpanded?: boolean;
  onToggleCollapse?: (expanded: boolean) => void;
  isDragOver?: boolean;
  isPotentialDropTarget?: boolean;
  isDropTarget?: boolean; // Enable droppable for cross-section drops
  dragListeners?: SyntheticListenerMap; // Drag listeners from parent SortableWidget
  // Callbacks
  onUpdateWidget?: (widgetId: string, updates: Partial<DashboardWidget>) => void;
  onDeleteWidget?: (widgetId: string) => void;
  onConfigureWidget?: (widgetId: string) => void;
  onMoveToSection?: (widgetId: string, sectionId: string | null, columnIndex?: number) => void;
  onReorderWidgets?: (updates: Array<{ id: string; sort_order: number; column_index?: number }>) => void;
  onExpandWidget?: (widgetId: string) => void; // Expand widget to fullscreen
  availableSections?: SectionInfo[];
}

const SectionWidget: React.FC<SectionWidgetProps> = ({
  widget,
  householdId,
  editMode = false,
  allWidgets = [],
  isExpanded: controlledExpanded,
  onToggleCollapse,
  isDragOver = false,
  isPotentialDropTarget = false,
  isDropTarget = false,
  dragListeners,
  onUpdateWidget,
  onDeleteWidget,
  onConfigureWidget,
  onMoveToSection,
  onReorderWidgets,
  onExpandWidget,
  availableSections = [],
}) => {
  const config = (widget.config || {}) as Partial<SectionConfig>;
  const {
    label = 'Section',
    collapsible = true,
    defaultExpanded = true,
    showTitle = true,
    showDivider = true,
    backgroundColor,
    shadow = 'none',
    gap = 16,
    padding = 16,
    verticalAlign = 'top',
  } = config;

  // Helper to format size values - handles both numbers and CSS strings
  const formatSize = (value: number | string | undefined, fallback: number): string => {
    if (value === undefined || value === null) return `${fallback}px`;
    if (typeof value === 'string') {
      // Already a CSS value like "20px", "1.5rem", etc.
      return value;
    }
    // Numeric value - add px suffix
    return `${value}px`;
  };

  // Compute column widths from config
  const columnWidths = getColumnWidths(config);
  const columnCount = columnWidths.length;

  // Get child widgets for this section
  const childWidgets = allWidgets.filter(w => w.parent_widget_id === widget.id);
  const childCount = childWidgets.length;

  // Use controlled state if provided, otherwise use internal state
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(label);

  // Popover menu state
  const [showPopover, setShowPopover] = useState(false);
  const [popoverEvent, setPopoverEvent] = useState<MouseEvent | null>(null);

  // Update editLabel when config label changes
  useEffect(() => {
    setEditLabel(label);
  }, [label]);

  // Make section droppable for cross-section drops from parent DndContext
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `section-drop-${widget.id}`,
    disabled: !isDropTarget || !editMode,
    data: {
      type: 'section',
      sectionId: widget.id,
    },
  });

  const handleToggle = () => {
    if (collapsible) {
      const newExpanded = !isExpanded;
      if (onToggleCollapse) {
        onToggleCollapse(newExpanded);
      } else {
        setInternalExpanded(newExpanded);
      }
    }
  };

  const handleLabelClick = (e: React.MouseEvent) => {
    if (editMode) {
      e.stopPropagation();
      setIsEditing(true);
      setEditLabel(label);
    }
  };

  const handleLabelBlur = () => {
    setIsEditing(false);
    if (editLabel !== label && onUpdateWidget) {
      onUpdateWidget(widget.id, {
        config: { ...config, label: editLabel }
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLabelBlur();
    } else if (e.key === 'Escape') {
      setEditLabel(label);
      setIsEditing(false);
    }
  };

  // Open menu popover
  const openPopover = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPopoverEvent(e.nativeEvent);
    setShowPopover(true);
  };

  // Handle configure click (full modal)
  const handleConfigure = () => {
    setShowPopover(false);
    onConfigureWidget?.(widget.id);
  };

  // Handle delete click
  const handleDelete = () => {
    setShowPopover(false);
    onDeleteWidget?.(widget.id);
  };

  // Quick config update helpers
  const updateConfig = (key: string, value: unknown) => {
    if (onUpdateWidget) {
      onUpdateWidget(widget.id, {
        config: { ...config, [key]: value }
      });
    }
  };

  // Handle column count change
  const handleColumnCountChange = (value: string) => {
    if (!onUpdateWidget) return;
    const numCols = parseInt(value, 10);
    if (isNaN(numCols) || numCols < 1 || numCols > 4) return;

    // Create equal-width columns based on count
    const equalWidth = Math.floor(12 / numCols);
    const newColumns = Array(numCols).fill(equalWidth);
    // Distribute remainder to first columns
    const remainder = 12 - (equalWidth * numCols);
    for (let i = 0; i < remainder; i++) {
      newColumns[i]++;
    }

    onUpdateWidget(widget.id, {
      config: { ...config, columns: newColumns, columnCount: numCols }
    });
  };

  // Handle individual column width change
  const handleColumnWidthChange = (colIndex: number, value: string) => {
    if (!onUpdateWidget) return;
    const newWidth = parseInt(value, 10);
    if (isNaN(newWidth)) return;

    // Get current columns or create from columnCount
    const currentColumns = config.columns && Array.isArray(config.columns)
      ? [...config.columns]
      : Array(columnCount).fill(Math.floor(12 / columnCount));

    currentColumns[colIndex] = newWidth;

    onUpdateWidget(widget.id, {
      config: { ...config, columns: currentColumns }
    });
  };

  // Get the current columns array for display
  const displayColumns = config.columns && Array.isArray(config.columns)
    ? config.columns
    : columnWidths;

  // Build CSS grid template for columns
  const gridTemplateColumns = columnWidths.map(w => `${w}fr`).join(' ');

  // Shadow class mapping
  const shadowClass = shadow !== 'none' ? `shadow-${shadow}` : '';

  const classNames = [
    'section-widget',
    collapsible ? 'collapsible' : '',
    editMode ? 'editing' : '',
    !isExpanded ? 'collapsed' : '',
    isDragOver || isOver ? 'drag-over' : '',
    isPotentialDropTarget ? 'potential-drop-target' : '',
    shadowClass,
  ].filter(Boolean).join(' ');

  // Inline styles for dynamic values
  const sectionStyle: React.CSSProperties = {
    ...(backgroundColor ? { backgroundColor } : {}),
  };

  return (
    <div className={classNames} style={sectionStyle}>
      {/* Section Header - always shown in edit mode, otherwise based on showTitle */}
      {/* The header acts as the drop zone for cross-section drops (outside child DndContext) */}
      {(showTitle || editMode) && (
        <div
          ref={setDropRef}
          className={`section-header ${isOver ? 'drop-target-active' : ''}`}
          onClick={handleToggle}
        >
          {/* Drag handle for reordering sections - only in edit mode */}
          {editMode && dragListeners && (
            <div
              className="section-drag-handle"
              {...dragListeners}
              onClick={(e) => e.stopPropagation()}
            >
              <IonIcon icon={reorderFourOutline} />
            </div>
          )}

          {collapsible && (
            <IonIcon
              icon={isExpanded ? chevronDownOutline : chevronForwardOutline}
              className="collapse-icon"
            />
          )}

          {isEditing ? (
            <input
              type="text"
              className="section-label-input"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              onBlur={handleLabelBlur}
              onKeyDown={handleKeyDown}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              className="section-label"
              onClick={handleLabelClick}
              title={editMode ? 'Click to edit' : undefined}
            >
              {label}
            </span>
          )}

          {/* Divider line OR spacer - ensures menu stays at right */}
          {showDivider ? (
            <div className="section-line" />
          ) : (
            <div className="section-spacer" />
          )}

          {/* Drop indicator when dragging over */}
          {isOver && (
            <span className="section-drop-indicator">Drop here</span>
          )}

          {/* Show widget count badge when collapsed */}
          {!isExpanded && childCount > 0 && !isOver && (
            <IonBadge color="medium" className="section-count-badge">
              {childCount} widget{childCount !== 1 ? 's' : ''}
            </IonBadge>
          )}

          {/* 3-dot menu - only in edit mode */}
          {editMode && (
            <IonButton
              fill="clear"
              size="small"
              className="section-menu-btn"
              onClick={openPopover}
            >
              <IonIcon icon={ellipsisVertical} />
            </IonButton>
          )}
        </div>
      )}

      {/* Drop zone when title is hidden - provides a target area when header is not shown */}
      {!showTitle && !editMode && isPotentialDropTarget && (
        <div
          ref={setDropRef}
          className={`section-drop-zone ${isOver ? 'drop-target-active' : ''}`}
        >
          {isOver && <span>Drop to add to section</span>}
        </div>
      )}

      {/* Section Menu Popover */}
      <IonPopover
        isOpen={showPopover}
        event={popoverEvent}
        onDidDismiss={() => setShowPopover(false)}
        className="section-options-popover"
      >
        <IonList lines="none" className="section-menu-list">
          {/* Column Count */}
          <IonItem className="section-menu-item">
            <IonLabel>Columns</IonLabel>
            <IonSelect
              value={String(columnCount)}
              onIonChange={(e) => handleColumnCountChange(e.detail.value)}
              interface="popover"
              className="section-column-select"
            >
              <IonSelectOption value="1">1</IonSelectOption>
              <IonSelectOption value="2">2</IonSelectOption>
              <IonSelectOption value="3">3</IonSelectOption>
              <IonSelectOption value="4">4</IonSelectOption>
            </IonSelect>
          </IonItem>

          {/* Column Widths - show individual width selectors for each column */}
          {columnCount > 1 && (
            <IonItem className="section-menu-item column-widths-row">
              <IonLabel>Widths</IonLabel>
              <div className="column-width-selects">
                {displayColumns.map((width, idx) => (
                  <IonSelect
                    key={idx}
                    value={String(width)}
                    onIonChange={(e) => handleColumnWidthChange(idx, e.detail.value)}
                    interface="popover"
                    className="section-width-select"
                  >
                    <IonSelectOption value="2">2</IonSelectOption>
                    <IonSelectOption value="3">3</IonSelectOption>
                    <IonSelectOption value="4">4</IonSelectOption>
                    <IonSelectOption value="5">5</IonSelectOption>
                    <IonSelectOption value="6">6</IonSelectOption>
                    <IonSelectOption value="7">7</IonSelectOption>
                    <IonSelectOption value="8">8</IonSelectOption>
                    <IonSelectOption value="9">9</IonSelectOption>
                    <IonSelectOption value="10">10</IonSelectOption>
                  </IonSelect>
                ))}
                <span className="column-width-total">= {displayColumns.reduce((a, b) => a + b, 0)}/12</span>
              </div>
            </IonItem>
          )}

          {/* Vertical Alignment */}
          <IonItem className="section-menu-item">
            <IonIcon icon={swapVerticalOutline} slot="start" />
            <IonLabel>Align</IonLabel>
            <IonSelect
              value={verticalAlign}
              onIonChange={(e) => updateConfig('verticalAlign', e.detail.value)}
              interface="popover"
              className="section-align-select"
            >
              <IonSelectOption value="top">Top</IonSelectOption>
              <IonSelectOption value="middle">Middle</IonSelectOption>
              <IonSelectOption value="bottom">Bottom</IonSelectOption>
            </IonSelect>
          </IonItem>

          <IonItemDivider className="section-menu-divider" />

          {/* Toggle: Show Title */}
          <IonItem className="section-menu-item">
            <IonIcon icon={textOutline} slot="start" />
            <IonLabel>Show Title</IonLabel>
            <IonToggle
              checked={showTitle}
              onIonChange={(e) => updateConfig('showTitle', e.detail.checked)}
            />
          </IonItem>

          {/* Toggle: Show Divider */}
          <IonItem className="section-menu-item">
            <IonIcon icon={removeOutline} slot="start" />
            <IonLabel>Show Divider</IonLabel>
            <IonToggle
              checked={showDivider}
              onIonChange={(e) => updateConfig('showDivider', e.detail.checked)}
            />
          </IonItem>

          {/* Toggle: Collapsible */}
          <IonItem className="section-menu-item">
            <IonIcon icon={collapsible ? lockOpenOutline : lockClosedOutline} slot="start" />
            <IonLabel>Collapsible</IonLabel>
            <IonToggle
              checked={collapsible}
              onIonChange={(e) => updateConfig('collapsible', e.detail.checked)}
            />
          </IonItem>

          {/* Toggle: Start Expanded - only shown when collapsible is enabled */}
          {collapsible && (
            <IonItem className="section-menu-item">
              <IonIcon icon={playOutline} slot="start" />
              <IonLabel>Start Expanded</IonLabel>
              <IonToggle
                checked={defaultExpanded}
                onIonChange={(e) => updateConfig('defaultExpanded', e.detail.checked)}
              />
            </IonItem>
          )}

          <IonItemDivider className="section-menu-divider" />

          {/* More Settings - Opens full modal */}
          {onConfigureWidget && (
            <IonItem button onClick={handleConfigure} className="section-menu-item">
              <IonIcon icon={settingsOutline} slot="start" />
              <IonLabel>More Settings...</IonLabel>
            </IonItem>
          )}

          {/* Delete section */}
          {onDeleteWidget && (
            <IonItem button onClick={handleDelete} className="section-menu-item danger-item">
              <IonIcon icon={trashOutline} slot="start" color="danger" />
              <IonLabel color="danger">Delete Section</IonLabel>
            </IonItem>
          )}
        </IonList>
      </IonPopover>

      {/* Section Columns Container - uses parent DndContext */}
      {isExpanded && (
        <div
          className="section-columns"
          style={{
            gridTemplateColumns,
            gap: formatSize(gap, 16),
            padding: formatSize(padding, 16),
            alignItems: verticalAlign === 'middle' ? 'center' : verticalAlign === 'bottom' ? 'end' : 'start',
          }}
        >
          {columnWidths.map((_, colIndex) => (
            <SectionColumn
              key={colIndex}
              sectionId={widget.id}
              columnIndex={colIndex}
              widgets={getWidgetsByColumn(allWidgets, widget.id, colIndex)}
              editMode={editMode}
              householdId={householdId}
              onUpdateWidget={onUpdateWidget}
              onDeleteWidget={onDeleteWidget}
              onConfigureWidget={onConfigureWidget}
              onMoveToSection={onMoveToSection}
              onExpandWidget={onExpandWidget}
              availableSections={availableSections}
              allWidgets={allWidgets}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SectionWidget;
